// Derives ordered unique values for the color legend based on current color-by field and layout.
import type { SwimlaneLayoutDTO, TimelineLayoutDTO } from '@tempo-io/roadmap-dto';
import { useMemo } from 'react';
import type { Item } from '@/lib/api/types/items';
import type { ISwimlaneHeader } from '@/lib/api/types/roadmapping';
import { getAllItemFieldValues } from '@/lib/utils/roadmapping/fieldExtraction';

type VisualizationLayout = SwimlaneLayoutDTO | TimelineLayoutDTO | null | undefined;

export function useUniqueHeaderValues(
  items: Item[],
  currentColorByField: string,
  isBaseColorSelected: boolean,
  matchingHeader: ISwimlaneHeader | null,
  layout: VisualizationLayout,
  preferItemOrderForHeaderValues = false,
): string[] {
  return useMemo(() => {
    if (!currentColorByField || isBaseColorSelected) return [];

    const values: string[] = [];
    const addedValues = new Set<string>();

    // Handle value extraction based on showEmptyHeaders and whether field is a header
    if (matchingHeader && 'options' in matchingHeader) {
      // For header fields, we need to match swimlane's exact behavior unless overridden

      if (layout?.showEmptyHeaders) {
        // When showEmptyHeaders is ON, use ALL header options
        (matchingHeader.options as Array<{ id: number | string; name: string }>).forEach(
          (option) => {
            const optionId = option.id.toString();
            if (!addedValues.has(optionId)) {
              values.push(optionId);
              addedValues.add(optionId);
            }
          },
        );

        // For Team, Status, Custom fields, AND ParentItem, add "No Value" header
        if (
          (matchingHeader.type === 'CustomPropertySelectorByID' ||
            matchingHeader.type === 'ParentItemSelector') &&
          !addedValues.has('null')
        ) {
          values.push('null'); // This is the NO_VALUE_ID constant from swimlane
          addedValues.add('null');
        }
      } else if (!preferItemOrderForHeaderValues) {
        // When showEmptyHeaders is OFF, only include values that exist in items
        // BUT we need to preserve the backend order from options
        // Use getAllItemFieldValues to capture ALL values from multi-value fields
        const itemValues = new Set<string>();
        items.forEach((item: Item) => {
          for (const v of getAllItemFieldValues(item, currentColorByField)) {
            itemValues.add(v);
          }
        });

        // Add values in the order they appear in options (backend order)
        (matchingHeader.options as Array<{ id: number | string; name: string }>).forEach(
          (option) => {
            const optionId = option.id.toString();
            if (itemValues.has(optionId) && !addedValues.has(optionId)) {
              values.push(optionId);
              addedValues.add(optionId);
            }
          },
        );

        // Check if any items would fall into the "No Value" category
        if (
          matchingHeader.type === 'CustomPropertySelectorByID' ||
          matchingHeader.type === 'ParentItemSelector'
        ) {
          const hasItemsWithoutValue = items.some(
            (item: Item) => getAllItemFieldValues(item, currentColorByField).length === 0,
          );
          if (hasItemsWithoutValue && !addedValues.has('null')) {
            values.push('null'); // Add the "No Value" option at the end
            addedValues.add('null');
          }
        }
      } else {
        // When showEmptyHeaders is OFF and we want to preserve item-derived ordering,
        // use the same behavior as non-header fields.
        items.forEach((item: Item) => {
          for (const fieldValue of getAllItemFieldValues(item, currentColorByField)) {
            if (!addedValues.has(fieldValue)) {
              values.push(fieldValue);
              addedValues.add(fieldValue);
            }
          }
        });

        if (
          matchingHeader.type === 'CustomPropertySelectorByID' ||
          matchingHeader.type === 'ParentItemSelector'
        ) {
          const hasItemsWithoutValue = items.some(
            (item: Item) => getAllItemFieldValues(item, currentColorByField).length === 0,
          );
          if (hasItemsWithoutValue && !addedValues.has('null')) {
            values.push('null');
            addedValues.add('null');
          }
        }
      }
    } else {
      // For non-header fields, extract from items
      items.forEach((item: Item) => {
        for (const fieldValue of getAllItemFieldValues(item, currentColorByField)) {
          if (!addedValues.has(fieldValue)) {
            values.push(fieldValue);
            addedValues.add(fieldValue);
          }
        }
      });
    }

    // For header fields, values are already in the correct order from above
    if (matchingHeader && 'options' in matchingHeader && !preferItemOrderForHeaderValues) {
      return values;
    }

    // For non-header fields, use alphabetical sort to match generateItemColorMap behavior
    // when no headers are passed (which happens for non-header fields in swimlane)
    return values.sort();
  }, [
    items,
    currentColorByField,
    isBaseColorSelected,
    matchingHeader,
    layout?.showEmptyHeaders,
    preferItemOrderForHeaderValues,
  ]);
}
